<template>
    <div id="about">
      <h1 v-if="this.$i18n.i18next.language === 'es'" id="title-understanding-es">{{ $t("understanding") }}</h1>
      <h1 v-if="this.$i18n.i18next.language === 'en'" id="title-understanding">{{ $t("understanding") }}</h1>
      <h1 id="title-mezcal" v-longclick="() => displayVersionNumber()">{{ $t("mezcal") }}</h1>
      <div id="sub-title">{{ $t("referenceGuide") }}</div>
      <div id="description-container">
        {{ $t("aGuideToBrands") }}<br />
        {{ $t("andMoreImportantly") }}<br />
        {{ $t("thePeopleWho") }}<br />
      </div>
      <h2 id="about-label">About:</h2>
      <div v-if="currentLanguage === 'en'">
        <div v-for="heading in headings" v-bind:key="heading.name">
          <div class="dropdown-link" @click="expandCollapseContainer(heading)"> {{ heading.name }}</div>
          <div class="container-collapse" :class="{ expanded: heading.isExpanded }">
            <div class="content" v-html="heading.content"></div>
          </div>
        </div>
      </div>
      <div v-if="currentLanguage === 'es'">
        <div v-for="heading in headingsEs" v-bind:key="heading.name">
          <div class="dropdown-link" @click="expandCollapseContainer(heading)"> {{ heading.name }}</div>
          <div class="container-collapse" :class="{ expanded: heading.isExpanded }">
            <div class="content" v-html="heading.content"></div>
          </div>
        </div>
      </div>
      <div v-if="displayVersion">Data version: {{version}}</div>
    </div>
</template>

<script>


export default {
  name: 'About',
  components: {

  },
  computed: {
    currentLanguage(){
      return this.$i18n.i18next.language
    }
  },
  async mounted (){
    this.version = await this.$getItem('versionNumber');
  },
  data() {
      return {
        version: '',
        displayVersion: false,
        headings: [
          {
            name: 'App',
            isExpanded: false,
            content: `
            While certainly capable of standing on its own, this app is designed as a companion to 
            James Schroeder's 2018 book "Understanding Mezcal." While the book itself focuses on 
            big-picture information about Mexico's hand-crafted agave spirits and the process by 
            which they're made, the aim of this app is to provide a quick, handy reference guide to 
            the granular specifics of the mezcal world. Namely: who makes what, where, and how. 
            <br /> 
            <br />
            The app is built around a comprehensive database of mezcaleros, brands, techniques, 
            gps coordinates, and agave names. The database will be frequently updated with the most 
            current information as the commercialization of Mexico's endemic spirits traditions unfolds.
            `
          },
          {
            name: 'Agave', 
            isExpanded: false, 
            content: `
              Botany is confusing. Doubly so when it comes to mezcal. This section aims to provide a 
              (by-no-means complete) pocket reference for the hundreds of species of agave used to 
              make spirits, and the plethora of names by which they're called.
            `
          },
          {
            name: 'Producer', 
            isExpanded: false, 
            content: `
              Agave spirits are ultimately a reflection of the people who make them. Connecting together 
              mezcal (and sotol, and raicilla, and bacanora...) producers with the commercially available 
              wares they produce is often tricky. This section aims to provide a quick (and growing) 
              reference to who makes what, and where.
            `
          },
          {
            name: 'Mezcal Maps', 
            isExpanded: false, 
            content: `
              Mezcal is a story of people and a place. 'And where is this one made?' 'Mexico.' 
              Mexico is MASSIVE. It's simply impossible to overstate the diversity of culture and climate 
              found within. A lot can be learned exploring the geographic connections - or lack thereof - 
              between producers.
            `
          },
          {
            name: 'Feedback',
            isExpanded: false, 
            content: `If you see something that's not listed, or any information you believe to be incorrect <a style="color:#fd9766;font-size:1.25rem;display:inline;line-height:0;" href="https://docs.google.com/forms/d/e/1FAIpQLSf4SwEkUN-UfhmvBonqrr5wBiige4gg4zhkcuFiIuv9Hp5Aug/viewform?vc=0&c=0&w=1&flr=0&usp=mail_form_link" target="_blank">click here</a> to submit a correction. Please note: in order for a brand to be listed, both the <b>producer's name</b> and <b>exact town</b> must be given.`
          }
        ],
        headingsEs: [
          {
            name: 'App',
            isExpanded: false,
            content: `
            Por supuesto que puedes usarlo de forma independiente, pero esta aplicación está diseñada como 
            complemento del libro "Acercándonos al Mezcal" de James Schroeder. El tema del libro es información 
            sobre los espíritus de México que se elaboran a mano, y el proceso en el que se crean. Pero esta 
            aplicación va a ser diferente. La app tiene como objetivo proveer una guía de referencia rápida sobre 
            los productores y las marcas de mezcal. Esto es: quien produce que, donde, y como.
            <br /> 
            <br />
            La aplicación está construyendo alrededor de una base de datos de mezcaleros, marcas, técnicas, coordenadas 
            GPS, y nombres de tipos de agave. Esta base de datos se actualizará con frecuencia, de acuerdo con la 
            información más reciente a medida que las tradiciones de los espíritus de México se cambian con su 
            comercialización.
            `
          },
          {
            name: 'Agave', 
            isExpanded: false, 
            content: `
              La botánica nos confunde. Más cuando aprendemos sobre el mundo de mezcal. Esta sección está pensada como 
              una guía (de ninguna manera completa) de los cientos de especies de agaves que se utilizan para hacer 
              bebidas espirituosas, y los nombres por los que se llaman.
            `
          },
          {
            name: 'Productores', 
            isExpanded: false, 
            content: `
              Los destilados de agave reflejan a las personas que los crean. Conectar un productor de mezcal (o sotol, 
              o raicilla, o bacanora...) con las marcas que produce puede ser difícil. Esta sección está pensada como 
              una referencia creciendo a quienes está produciendo que, y donde.
            `
          },
          {
            name: 'Mapas de Mezcal', 
            isExpanded: false, 
            content: `
              El mezcal es una historia de gente y tierra. ¿Y de dónde sacan esto?' 'México'. Esa no es una respuesta 
              suficiente. México es enorme. Nadie puede contar la diversidad de clima y cultura encontrada dentro. 
              Podemos descubrir mucho cuando exploramos las conexiones - o la falta de ella - entre algunos productores.
            `
          },
          {
            name: 'Feedback',
            isExpanded: false, 
            content: `Si encuentras algo que no está listado, o algo que parece estar mal, <a style="color:#fd9766;font-size:1.25rem;display:inline;line-height:0;" href="https://docs.google.com/forms/d/e/1FAIpQLSf4SwEkUN-UfhmvBonqrr5wBiige4gg4zhkcuFiIuv9Hp5Aug/viewform?vc=0&c=0&w=1&flr=0&usp=mail_form_link" target="_blank">haz clic aquí</a> para enviar una corrección. Tenga en cuenta: necesitamos el <b>nombre del productor</b> y <b>el pueblo específico en la que vive</b> para incluir una marca aquí.`
          }
        ]
      
    }
  },
  methods: {
    expandCollapseContainer(heading){
      heading.isExpanded = !heading.isExpanded;
      const anyContentExpanded = this.headings.find(heading => heading.isExpanded === true)
      const anyContentExpandedEs = this.headingsEs.find(heading => heading.isExpanded === true)
      if (anyContentExpanded === undefined && anyContentExpandedEs === undefined){
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    displayVersionNumber(){
      this.displayVersion = true;
    },

  }
}

</script>

<style lang="scss" scoped>

h1, h3, h4, h5 {
  margin-bottom: 0;
}

// .description {
//   margin-top: -5px;
// }

.dropdown-link{
  font-size: 1.5rem;
  // background: #9cd3cd;
  border-bottom: 1px solid grey;
  box-sizing: border-box;
  padding-left: 5px;
}

.container-collapse {
  max-height: 0px;
  transition: all 0.5s ease-in-out;
  overflow: hidden;
  border-left: 1px solid grey;
  border-right: 1px solid grey;
  box-sizing: border-box;
  background: white;
}

.container-collapse.expanded {
  max-height: 800px;
  border-bottom: 1px solid grey;
}

.content {
  padding: 10px;
  font-size: 1.25rem;
}

#about {
  margin: 5px;
  margin-bottom: 250px;
}


#sub-title {
  margin-top: -9px;
}

#description-container {
  margin-top: 30px;
  margin-bottom: 30px;
  font-size: 1.75rem;
  font-family: 'Avenir'
}

.feedback {
  font-size: 1rem;
  display: inline;
  color: #9cd3cd;
}

#title-understanding {
  font-family: 'Avenir';
  font-size: 23px;
}

#title-understanding-es{
  font-family: 'Avenir';
  font-size: 26px;
}

#title-mezcal {
  font-size: 51px;
  margin-top: -9px;

}

</style>